import * as React from 'react'
import { ChangeEvent } from 'react'

import { useTheme } from '@mui/material/styles'

import { ENTITY_TYPES } from '@sales-center/common'
import { SettingCardOptions } from '@user/components/Settings/EntityFormSettings/FieldSettingsCard/SettingCardOptions'
import {
  buildNameRequirementOptions,
  getNameRequirementMode,
  getRequiredNameFields,
} from '@user/components/Settings/EntityFormSettings/config/nameRequirementMode'
import type { IEntityFormSetting } from '@user/store/entityFormSettings/typings'

import { getStyles } from './styling'

interface NameRequirementModeFieldProps {
  settingItem: IEntityFormSetting
  allSettings: IEntityFormSetting[]
  entityType: ENTITY_TYPES
  onChangeNameMode: (
    event: ChangeEvent<HTMLInputElement>,
    value: string,
  ) => void
  isTidalThemeActive: boolean
}

/**
 * The Company Name field's own settings UI, letting an admin choose whether
 * the currently-required name-group fields (First Name / Last Name / Company
 * Name) must *all* be filled in (`and`) or whether *any one* of them satisfies
 * the requirement (`or`).
 *
 * Selected by the registry's `nameRequirementMode` renderer on the `company`
 * row. Renders nothing when fewer than two of the name-group fields are
 * currently required — the mode is only meaningful once there's a choice
 * between "all of these" and "any one of these" to make.
 */
export function NameRequirementModeField(props: NameRequirementModeFieldProps) {
  const { allSettings, entityType, onChangeNameMode, isTidalThemeActive } =
    props
  const theme = useTheme()
  const styles = getStyles(theme, isTidalThemeActive)

  const requiredFields = getRequiredNameFields(allSettings, entityType)
  if (requiredFields.length < 2) return null

  return (
    <SettingCardOptions
      value={getNameRequirementMode(allSettings, entityType)}
      onChangeOption={onChangeNameMode}
      options={buildNameRequirementOptions(requiredFields)}
      marginLeft={styles.props.opportunityStageMarginLeft}
      isTidalThemeActive={isTidalThemeActive}
      inlineRadioButtonsOnly
    />
  )
}
